/** Audience segments and status presentation, shared by the composer and the history. */

import { guestStatus } from '../../../lib/guestFilter';
import type { TrackerGuest } from '../../../services/tracker-api-client';

/**
 * "Everyone", spelled as a real value rather than as the empty string.
 *
 * It used to be `''`, which MUI's select renders as *nothing chosen*: picking
 * "All guests" left the field looking blank and gave the host no confirmation
 * they had chosen anything. It was selected — it just could not be seen. The
 * wire still carries null, which is what the API reads as "no segment filter";
 * only the UI value changed.
 */
export const AUDIENCE_ALL = 'all';

/**
 * Guests who said yes.
 *
 * Not a segment. Segments are a host-assigned label on the roster; this is the
 * guest's own answer, and the two disagree constantly — a roster of 137 with 75
 * declines has no segment that means "the 40 who are coming". Selecting it
 * resolves to an explicit invitee id list (see {@link audienceInviteeIds}),
 * which the broadcast API already honours, so it needs nothing from the server.
 */
export const AUDIENCE_RSVP_GOING = 'rsvp:going';

/** The stored value for a yes. `maybe` and `unknown` are deliberately not it. */
export const RSVP_GOING = 'going';

/**
 * Guests who have never answered.
 *
 * The chase list, and the one cohort a host needs weekly in the run-up to a
 * trip. Like {@link AUDIENCE_RSVP_GOING} it is not a segment — it is the
 * absence of the guest's own answer — so it resolves to an explicit id list.
 */
export const AUDIENCE_RSVP_NO_REPLY = 'rsvp:no_reply';

/**
 * Audiences that resolve to an explicit id list rather than to a roster
 * segment. Registered once: forgetting to add one here put its value on the
 * wire as a segment no guest carries, which targets nobody with no error.
 */
export const RESOLVED_AUDIENCES: ReadonlySet<string> = new Set([
  AUDIENCE_RSVP_GOING,
  AUDIENCE_RSVP_NO_REPLY,
]);

export const AUDIENCE_UNCHOSEN = 'unchosen';

export const AUDIENCES = [
  { value: AUDIENCE_ALL, label: 'All guests' },
  { value: AUDIENCE_RSVP_GOING, label: "RSVP'd yes" },
  { value: AUDIENCE_RSVP_NO_REPLY, label: 'No reply yet' },
  { value: 'core', label: 'Core guests' },
  { value: 'travel_party', label: 'Travel party' },
];

/**
 * What Reuse should put in the dropdown for a past broadcast.
 *
 * A real segment round-trips exactly. A null one cannot — see
 * {@link AUDIENCE_UNCHOSEN} — so it comes back as the refusal rather than as
 * the widest possible reading of itself.
 */
export function audienceForReuse(pastSegment: string | null | undefined): string {
  if (!pastSegment) return AUDIENCE_UNCHOSEN;
  return AUDIENCES.some((option) => option.value === pastSegment) ? pastSegment : AUDIENCE_UNCHOSEN;
}

/**
 * What Reuse should put in the dropdown for a SCHEDULED row (an in-doubt reminder being re-sent by hand).
 *
 * Unlike a sent broadcast, a scheduled row records its audience unambiguously: an RSVP kind (`audienceKind`, decided when it fired), else a roster
 * segment, else — no kind, no segment, no guest list at all — everyone. An explicit `inviteeIds` list (even an empty one) cannot be restored by an option in the
 * dropdown, so it comes back as the refusal ({@link AUDIENCE_UNCHOSEN}) and the host picks, never as the widest reading of it. A segment that is not
 * one of the options is refused too.
 */
export function audienceForScheduledReuse(row: {
  audienceKind?: string | null;
  segment?: string | null;
  inviteeIds?: readonly string[] | null;
}): string {
  if (row.audienceKind === 'rsvp_going') return AUDIENCE_RSVP_GOING;
  if (row.audienceKind === 'rsvp_no_reply') return AUDIENCE_RSVP_NO_REPLY;
  if (row.audienceKind) return AUDIENCE_UNCHOSEN;                       // a kind this Studio does not know: never guess
  if (row.inviteeIds != null) return AUDIENCE_UNCHOSEN;                // ANY explicit list, even an empty one: not an option the dropdown has
  if (!row.segment) return AUDIENCE_ALL;
  return AUDIENCES.some((option) => option.value === row.segment) ? (row.segment as string) : AUDIENCE_UNCHOSEN;
}

/** False while the composer is holding an audience nobody chose. */
export function audienceIsChosen(audience: string): boolean {
  return Boolean(audience) && audience !== AUDIENCE_UNCHOSEN;
}

/**
 * The `segment` to put on the wire. "All guests" and the RSVP audience both
 * send null — neither is a roster segment, and sending "all" or "rsvp:going" as
 * one would match no guest and silently target nobody.
 */
export function audienceSegment(audience: string): string | null {
  if (!audience || audience === AUDIENCE_ALL || RESOLVED_AUDIENCES.has(audience)) return null;
  // AUDIENCE_UNCHOSEN falls through deliberately. The composer refuses to arm
  // while it is selected, so it should never get here — but if it ever does, a
  // segment no guest carries targets nobody, and sending to nobody is the right
  // way for this particular mistake to fail. Mapping it to null would target
  // all 137.
  return audience;
}

/**
 * Explicit recipients, or undefined to let the segment decide.
 *
 * Resolved through `guestStatus` — the SAME function the roster filter bar
 * uses — rather than by reading `rsvpStatus` here. That is not tidiness; it is
 * the whole correctness argument:
 *
 * `group_guest_tracking.going_override` is a separate column from
 * `group_invitees.rsvp_status`, and the value everything host-facing shows is
 * `going_override ?? map(rsvp_status)`. When a host works the Guests tab's
 * Going dropdown — the primary workflow — the override is written and
 * rsvp_status is left alone. Reading rsvp_status directly therefore misses
 * every host decision: the roster would say "No reply: 97" while this said 137,
 * and the chase message would go to the forty people the host had personally
 * marked as coming.
 *
 * It also settles the vocabulary. `guestStatus` knows the import synonyms
 * (`yes`/`no`/`not_going`) and the tracker's own words; matching a literal
 * 'going' here meant a Partiful roster resolved "RSVP'd yes" to nobody while
 * those same guests were also excluded from the chase list — unreachable
 * through either audience.
 *
 * Returns `[]`, never undefined, for a chosen RSVP audience that matched
 * nobody: undefined means "no id filter", which the API reads as EVERYONE.
 */
export function audienceInviteeIds(
  audience: string,
  guests: ReadonlyArray<TrackerGuest> | null | undefined,
): string[] | undefined {
  if (audience === AUDIENCE_RSVP_GOING) {
    return (guests ?? [])
      .filter((guest) => guestStatus(guest) === 'going')
      .map((guest) => guest.inviteeId);
  }
  if (audience === AUDIENCE_RSVP_NO_REPLY) {
    return (guests ?? [])
      .filter((guest) => {
        // A plus-one has no invitation of their own, so they have nothing to
        // reply to and carry a blank status forever. Chasing them weekly is
        // asking a question that was never put to them; their inviter is the
        // one who answers for both.
        if (guest.plusOneOfInviteeId) return false;
        return guestStatus(guest) === 'no_reply';
      })
      .map((guest) => guest.inviteeId);
  }
  return undefined;
}

export function audienceLabel(segment: string | null | undefined): string {
  const value = segment || AUDIENCE_ALL;
  return AUDIENCES.find((audience) => audience.value === value)?.label || value;
}

export function statusColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  if (status === 'sent') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'skipped') return 'warning';
  return 'default';
}
